import { IActionContext, createObservableDataAction, IAction, IGeneric, IAny, ICreateActionContext } from "@msdyn365-commerce/core";
import { CacheType, IActionInput, ProductsDataActions } from "@msdyn365-commerce/retail-proxy";
import { getSelectedProductIdFromActionInput } from "@msdyn365-commerce-modules/retail-actions";
import { getRelationTypesAsync } from "@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g";
import { SupportedRelationTypes } from "./get-relation-types-by-ids";
import { ProductSearchResultExt } from "../shared-utils/types";
import { arrayUniqueByProperty } from "../shared-utils/common-utils";
import { toJS } from "mobx";

export class GetReplacementsByProductIdInput implements IActionInput {

    public productId: number;

    public constructor(productId: number) {
        this.productId = productId;
    }

    public shouldCacheOutput = (): boolean => false;

    public getCacheKey = () => `ReplacementsByProductId`;
    public getCacheObjectType = () => 'ReplacementsByProductId';
    public dataCacheType = (): CacheType => 'request';
}

const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetReplacementsByProductIdInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new GetReplacementsByProductIdInput(+productId);
    }

    throw new Error('Unable to create GetReplacementsByProductIdInput, no productId found on module config or query');
};

export const getReplacementsByProductIdAction = async (input: GetReplacementsByProductIdInput, ctx: IActionContext): Promise<ProductSearchResultExt[]> => {
    if (!ctx.requestContext.user.isAuthenticated) {
        return [];
    }
    let types = await getRelationTypesAsync(
        { callerContext: ctx },
        input.productId,
        ctx.requestContext.apiSettings.channelId,
        ctx.requestContext.apiSettings.catalogId);

    types = types.filter(type => Object.values(SupportedRelationTypes).includes(type.Name as SupportedRelationTypes));

    if (types.length === 0) {
        return [];
    }

    const promises = types.map(async type => {
        const results = await ProductsDataActions.getRelatedProductsAsync(
            { callerContext: ctx },
            input.productId,
            ctx.requestContext.apiSettings.channelId,
            ctx.requestContext.apiSettings.catalogId,
            type.RecordId
        );
        results.forEach(p => {
            (p as ProductSearchResultExt).RelationTypes = [type];
        });
        return results;
    });

    const replacements = toJS((await Promise.all(promises))).flatMap(x => toJS(x));

    // Assumingly the same product can be related via different relation types, so flatten and deduplicate.
    // Take RelationTypes from all occurrences and merge into one.
    const seenRecIds = new Set<number>();
    const dedupedProducts: ProductSearchResultExt[] = [];

    replacements.forEach((prod) => {
        if (seenRecIds.has(prod.RecordId)) {
            // Already seen, merge RelationTypes by name
            const existingProd = dedupedProducts.find(x => x.RecordId === prod.RecordId);
            if (existingProd && (prod as ProductSearchResultExt).RelationTypes) {
                existingProd.RelationTypes = arrayUniqueByProperty([
                    ...(existingProd.RelationTypes || []),
                    ...(prod as ProductSearchResultExt).RelationTypes!
                ], 'Name');
            }
        } else {
            seenRecIds.add(prod.RecordId);
            dedupedProducts.push(prod);
        }
    });

    return dedupedProducts;
};

export const getReplacementsByProductId = createObservableDataAction({
    id: 'src/actions/get-replacements-by-product-id',
    action: <IAction<ProductSearchResultExt[]>>getReplacementsByProductIdAction,
    input: createInput
});

export default getReplacementsByProductId;
